import type { CompanyNote, CompanyNoteData } from "@/types/company/companyNote";

export class AccountReceivable {
  public id: number | null;
  public company_id: number;
  public client_name: string | null;
  public client_code: string | null;
  public vat_number: string | null;
  public country: string | null;
  public first_order_date: Date | null;
  public payment_term_display_name: string | null;
  public service_type: string | null;
  public dso_ytd: number | null;
  public dso_ly: number | null;
  public has_transport_contract: boolean;
  public has_warehousing_contract: boolean;
  public right_of_retention: boolean;
  public right_of_pledge: boolean;
  public klg_force_grey: boolean;
  public due_balance: number | null;
  public overdue_amount: number | null;
  public credit_limit: number | null;
  public credit_limit_exceeded: number | null;
  public last_invoice_date: Date | null;
  public marketing_ratio: string | null;
  public sales_ratio: string | null;
  public industry: string | null;
  public business_category: string | null;
  public key_account_manager: string | null;
  public klg_color: string | null;
  public score: number | null;
  public klg_score: number | null;
  public company?: {
    lastPaymentNote: CompanyNote | null;
  };

  constructor(data: AccountReceivableData | void) {
    if (data === undefined) {
      this.id = null;
      this.company_id = 0;
      this.client_name = null;
      this.client_code = null;
      this.vat_number = null;
      this.country = null;
      this.first_order_date = null;
      this.payment_term_display_name = null;
      this.service_type = null;
      this.dso_ytd = 0;
      this.dso_ly = 0;
      this.has_transport_contract = false;
      this.has_warehousing_contract = false;
      this.right_of_retention = false;
      this.right_of_pledge = false;
      this.klg_force_grey = false;
      this.due_balance = 0;
      this.overdue_amount = 0;
      this.credit_limit = 0;
      this.credit_limit_exceeded = 0;
      this.last_invoice_date = null;
      this.marketing_ratio = null;
      this.sales_ratio = null;
      this.industry = null;
      this.business_category = null;
      this.key_account_manager = null;
      this.klg_color = null;
      this.score = null;
      this.klg_score = null;
      this.company = undefined;
    } else {
      this.id = data.id;
      this.company_id = data.company_id;
      this.client_name = data.client_name;
      this.client_code = data.client_code;
      this.vat_number = data.vat_number;
      this.country = data.country;
      this.first_order_date = null;
      if (
        data.first_order_date !== null &&
        data.first_order_date !== undefined
      ) {
        this.first_order_date = new Date(data.first_order_date);
      }
      this.payment_term_display_name = data.payment_term_display_name;
      this.service_type = data.service_type;
      this.dso_ytd = data.dso_ytd;
      this.dso_ly = data.dso_ly;
      this.has_transport_contract = data.has_transport_contract;
      this.has_warehousing_contract = data.has_warehousing_contract;
      this.right_of_retention = data.right_of_retention;
      this.right_of_pledge = data.right_of_pledge;
      this.klg_force_grey = data.klg_force_grey;
      this.due_balance = data.due_balance;
      this.overdue_amount = data.overdue_amount;
      this.credit_limit = data.credit_limit;
      this.credit_limit_exceeded = data.credit_limit_exceeded;
      this.last_invoice_date = null;
      if (
        data.last_invoice_date !== null &&
        data.last_invoice_date !== undefined
      ) {
        this.last_invoice_date = new Date(data.last_invoice_date);
      }
      this.marketing_ratio = data.marketing_ratio;
      this.sales_ratio = data.sales_ratio;
      this.industry = data.industry;
      this.business_category = data.business_category;
      this.key_account_manager = data.key_account_manager;
      this.klg_color = data.klg_color;
      this.score = data.score;
      this.klg_score = data.klg_score;
      this.company = data.company;
    }
  }

  serializeData(
    without_relations: boolean | Array<string> = false,
    with_relations: boolean | Array<string> = false,
    ids_only: boolean = false,
  ): AccountReceivableData {
    const serialized_data = {
      id: this.id,
      company_id: this.company_id,
      client_name: this.client_name,
      client_code: this.client_code,
      vat_number: this.vat_number,
      country: this.country,
      first_order_date: this.first_order_date,
      payment_term_display_name: this.payment_term_display_name,
      service_type: this.service_type,
      dso_ytd: this.dso_ytd,
      dso_ly: this.dso_ly,
      has_transport_contract: this.has_transport_contract,
      has_warehousing_contract: this.has_warehousing_contract,
      right_of_retention: this.right_of_retention,
      right_of_pledge: this.right_of_pledge,
      klg_force_grey: this.klg_force_grey,
      due_balance: this.due_balance,
      overdue_amount: this.overdue_amount,
      credit_limit: this.credit_limit,
      credit_limit_exceeded: this.credit_limit_exceeded,
      last_invoice_date: this.last_invoice_date,
      marketing_ratio: this.marketing_ratio,
      sales_ratio: this.sales_ratio,
      industry: this.industry,
      business_category: this.business_category,
      key_account_manager: this.key_account_manager,
      klg_color: this.klg_color,
      score: this.score,
      klg_score: this.klg_score,
    };
    return serialized_data;
  }
}

export type AccountReceivableData = {
  id: number | null;
  company_id: number;
  client_name: string | null;
  client_code: string | null;
  vat_number: string | null;
  country: string | null;
  first_order_date: Date | null;
  payment_term_display_name: string | null;
  service_type: string | null;
  dso_ytd: number | null;
  dso_ly: number | null;
  has_transport_contract: boolean;
  has_warehousing_contract: boolean;
  right_of_retention: boolean;
  right_of_pledge: boolean;
  klg_force_grey: boolean;
  due_balance: number | null;
  overdue_amount: number | null;
  credit_limit: number | null;
  credit_limit_exceeded: number | null;
  last_invoice_date: Date | null;
  marketing_ratio: string | null;
  sales_ratio: string | null;
  industry: string | null;
  business_category: string | null;
  key_account_manager: string | null;
  klg_color: string | null;
  score: number | null;
  klg_score: number | null;
  company?: {
    lastPaymentNote: CompanyNoteData | null;
  };
};
